import { Mail, Edit, Trash2, RotateCcw } from "lucide-react";
import MoreButton from "../common/MoreButton";
import api from "../../services/api";

interface CollaboratorsTableProps {
    collaborators: Array<any>;
    isLoading: boolean;
    searchTerm: string;
    statusFilter: string;
    onEditClick: (collaborator: any) => void;
    onRefresh: () => void;
}

export default function CollaboratorsTable({
    collaborators,
    isLoading,
    searchTerm,
    statusFilter,
    onEditClick,
    onRefresh
}: CollaboratorsTableProps) {

    // Filtrar colaboradores
    const filteredCollaborators = collaborators.filter((collab) => {
        const matchesSearch =
            collab.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            collab.email?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === "all" || collab.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Formatar data
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '--';
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date);
    };

    // Status badge
    const getStatusBadge = (status: string) => {
        const statusMap: Record<string, { text: string, color: string }> = {
            'active': { text: 'Ativo', color: 'green' },
            'pending': { text: 'Pendente', color: 'yellow' },
            'inactive': { text: 'Inativo', color: 'gray' },
        };

        const { text, color } = statusMap[status] || { text: status, color: 'gray' };

        const colorClasses: Record<string, { bg: string, text: string }> = {
            green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
            yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
            gray: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-400' },
        };

        return (
            <div className={`px-3 py-1 text-sm ${colorClasses[color].text} ${colorClasses[color].bg} rounded-full inline-flex items-center`}>
                <span className="mr-1">●</span> {text}
            </div>
        );
    };

    // Reenviar convite
    const handleResendInvite = async (collaboratorId: string) => {
        try {
            await api.post(`/api/collaborators/${collaboratorId}/resend-invite`);
            alert('Convite reenviado com sucesso!');
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao reenviar convite';
            alert(errorMsg);
        }
    };

    // Desativar colaborador
    const handleDeactivate = async (collaboratorId: string) => {
        if (!confirm('Tem certeza que deseja desativar este colaborador?')) return;

        try {
            await api.delete(`/api/collaborators/${collaboratorId}`);
            alert('Colaborador desativado com sucesso!');
            onRefresh();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao desativar colaborador';
            alert(errorMsg);
        }
    };

    // Reativar colaborador
    const handleReactivate = async (collaboratorId: string) => {
        try {
            await api.patch(`/api/collaborators/${collaboratorId}/reactivate`);
            alert('Colaborador reativado com sucesso!');
            onRefresh();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao reativar colaborador';
            alert(errorMsg);
        }
    };

    if (isLoading) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="flex items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
                </div>
            </div>
        );
    }

    if (filteredCollaborators.length === 0) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 dark:border-gray-800 dark:bg-white/[0.03]">
                <p className="text-center text-gray-500 dark:text-gray-400">
                    {searchTerm || statusFilter !== "all"
                        ? "Nenhum colaborador encontrado com os filtros aplicados."
                        : "Nenhum colaborador cadastrado. Clique em 'Adicionar colaborador' para convidar alguém."}
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Colaborador
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Status
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Data do Convite
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Confirmado em
                            </th>
                            <th className="px-6 py-4 pr-8 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Ações
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                        {filteredCollaborators.map((collaborator) => (
                            <tr key={collaborator.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10">
                                            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                                <span className="text-blue-700 dark:text-blue-400 font-medium text-sm">
                                                    {collaborator.name?.[0]?.toUpperCase() || collaborator.email?.[0]?.toUpperCase() || 'C'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {collaborator.name || '---'}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {collaborator.email}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(collaborator.status)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(collaborator.invitedAt)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(collaborator.confirmedAt)}
                                </td>
                                <td className="px-6 py-4 pr-8 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end gap-2 relative">
                                        <MoreButton
                                            buttonClassName="p-2"
                                            options={[
                                                ...(collaborator.status === 'pending'
                                                    ? [{
                                                        label: 'Reenviar convite',
                                                        onClick: () => handleResendInvite(collaborator.id),
                                                        icon: <Mail className="size-4" />
                                                    }]
                                                    : []),
                                                ...(collaborator.status === 'active'
                                                    ? [
                                                        {
                                                            label: 'Editar permissões',
                                                            onClick: () => onEditClick(collaborator),
                                                            icon: <Edit className="size-4" />
                                                        },
                                                        {
                                                            label: 'Desativar',
                                                            onClick: () => handleDeactivate(collaborator.id),
                                                            icon: <Trash2 className="size-4" />,
                                                            className: 'text-red-600 dark:text-red-400'
                                                        }
                                                    ]
                                                    : []),
                                                ...(collaborator.status === 'inactive'
                                                    ? [{
                                                        label: 'Reativar',
                                                        onClick: () => handleReactivate(collaborator.id),
                                                        icon: <RotateCcw className="size-4" />,
                                                        className: 'text-green-600 dark:text-green-400'
                                                    }]
                                                    : [])
                                            ]}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
